import { useRef, useCallback, type KeyboardEvent, useEffect } from 'react';
import { ff } from '@atlassian/jira-feature-flagging';
import { fg } from '@atlassian/jira-feature-gating';

export const CELL_WRAPPER_TEST_ID = 'business-list.ui.list-view.cell-navigation.cell-container';

export const isCellWrapper = (element?: HTMLElement | Element | null) =>
	element?.getAttribute('data-testid') === CELL_WRAPPER_TEST_ID;

export const useMultiSelectKeyDown = ({
	editMode = true,
	wasEditMode = false,
	menuIsOpen,
	setMenuIsOpen,
	saveAndClose,
	onEscapeKeyPress,
}: {
	editMode?: boolean;
	wasEditMode?: boolean;
	menuIsOpen: boolean;
	setMenuIsOpen: (menuIsOpen: boolean) => void;
	saveAndClose: () => void;
	onEscapeKeyPress: () => void;
}) => {
	const shouldCloseWithoutSaving = useRef(false);
	const saveAndCloseRef = useRef(saveAndClose);
	saveAndCloseRef.current = saveAndClose;

	useEffect(() => {
		if (fg('fix-bug-labels-not-saving')) {
			shouldCloseWithoutSaving.current = false;
		}
	}, [editMode]);

	const saveOnBlur = useCallback(() => {
		// save on blur when user did not press Escape
		if (wasEditMode && !editMode && fg('jira_list_keyboard_nav')) {
			if (!shouldCloseWithoutSaving.current) {
				saveAndClose();

				// set close without saving to true because we already saved
				if (ff('jira.list.field-support-part1')) {
					shouldCloseWithoutSaving.current = true;
				}
			} else {
				shouldCloseWithoutSaving.current = false;
			}
		}
	}, [editMode, saveAndClose, wasEditMode]);

	const saveOnUnmount = useCallback(() => {
		// save on edit view unmount
		if (!shouldCloseWithoutSaving.current && fg('jira_list_keyboard_nav')) {
			saveAndCloseRef.current();
		} else {
			shouldCloseWithoutSaving.current = false;
		}
	}, []);

	const onKeyDown = useCallback(
		(event: KeyboardEvent<HTMLElement>) => {
			if (editMode) {
				// Close the menu on selecting an option
				if (menuIsOpen && ['Enter', 'Tab', 'Space'].includes(event.code)) {
					setMenuIsOpen(false);
					return true;
				}

				// save and close when menu is closed
				if (!menuIsOpen && ['Enter', 'Tab'].includes(event.code)) {
					saveAndClose();

					// set close without saving to true because we already saved
					if (ff('jira.list.field-support-part1')) {
						shouldCloseWithoutSaving.current = true;
					}

					return false;
				}

				// Reopen the menu when the user types or hits down arrow
				if (
					!menuIsOpen &&
					(event.code === 'ArrowDown' ||
						(event.code.startsWith('Key') && !event.metaKey && !event.ctrlKey))
				) {
					setMenuIsOpen(true);
					return true;
				}

				if (event.code === 'Escape') {
					shouldCloseWithoutSaving.current = true;
					onEscapeKeyPress && onEscapeKeyPress();
				}
			}

			return false;
		},
		[editMode, menuIsOpen, onEscapeKeyPress, saveAndClose, setMenuIsOpen],
	);

	return { onKeyDown, saveOnBlur, saveOnUnmount };
};
